import {
  catalogApi,
  deleteResource,
  fetchPage,
  patchResource,
  type CatalogRow,
  type ListParams,
} from './collections';
import { apiFetch, downloadFile } from './http';
import type { EntityReference } from './requests';

/**
 * The models of `FIN-13` and the fee and time of `AM-318`; the model decides whether an hour produces
 * a charge of its own.
 */
export const BILLING_MODELS = [
  // `AM-466` (C11-26): in the order of the billing model: Por hora, Abono más horas, Abono con paquete de horas, Solo
  // abono, Precio fijo, Paquete de horas sin abono, Sin cargo; every list of models reads this one.
  'time_and_material',
  'fee_and_time',
  'retainer',
  'subscription',
  'fixed_price',
  'included_hours',
  'non_billable',
] as const;

export type BillingModel = (typeof BILLING_MODELS)[number];

export const CONTRACT_STATUSES = ['draft', 'active', 'expired', 'terminated'] as const;

export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** What a customer agreed to pay for (05f §3). There is no `hourlyRate`: the price comes from the rate cards (`FIN-06d`). */
export interface Contract extends CatalogRow {
  organizationId: string;
  /**
   * The customer's name as it reads now, active or not, deleted or not: the list (`AM-367`) and the
   * ficha (`AM-371`) name the customer with it, also for whoever cannot read `/organizations`. Every
   * answer about a contract carries it, the commands too, so an answer kept in the cache never names
   * the customer less than the read did.
   */
  organizationName: string | null;
  contractNumber: string;
  name: string;
  contractType: string | null;
  status: ContractStatus;
  /** `yyyy-MM-dd`; `endDate` is the last day covered, inclusive (`AM-116`). */
  startDate: string;
  endDate: string | null;
  /**
   * `yyyy-MM-dd`: the day somebody wants to look at the contract again, usually its price (`AM-319`).
   * It renews and ends nothing; the billing manager is told 60, 30 and 7 days before.
   */
  reviewDate: string | null;
  currency: string;
  billingModel: BillingModel;
  billingDayOfMonth: number;
  billingTrigger: 'period_close' | 'ticket_closure';
  /**
   * Once a period ends, the worker calculates its close and leaves the draft invoice for a person to
   * review and issue (migration 0059). On by default for a fixed price or a subscription.
   */
  autoClosePeriods: boolean;
  includedMinutesPerPeriod: number | null;
  overageRate: number | null;
  monthlyAmount: number | null;
  paymentTermsDays: number | null;
  invoiceLineGrouping: string;
  slaPolicyId: string | null;
  notes: string | null;
  createdAt: string;
}

/** One service the contract names, either to cover it or to leave it out; the row is the statement. */
export interface ContractService {
  contractId: string;
  serviceId: string;
  /** The name the service has now, whatever its state: a contract keeps naming a deactivated one. */
  serviceName: string;
  inclusionType: 'included' | 'excluded';
  eTag: string;
  createdAt: string;
  updatedAt: string;
}

/** The two moments an amount that no hour produces can become money (`FIN-13d`). */
export const RECOGNITION_TRIGGERS = ['on_period_close', 'on_ticket_resolved'] as const;

export type RecognitionTrigger = (typeof RECOGNITION_TRIGGERS)[number];

/** An amount the contract charges that no hour produces, until the moment it is recognised (`FIN-13d`). */
export interface ContractFixedPriceItem extends CatalogRow {
  contractId: string;
  serviceOfferingId: string | null;
  ticketId: string | null;
  /** The ticket's own number, for a screen that shows and links it instead of the internal id (P068). */
  ticketNumber: string | null;
  description: string;
  amount: number;
  currency: string;
  recognitionTrigger: RecognitionTrigger;
  /** Not null once the item became money (`FIN-13d`): it is not deleted from that point on. */
  consumedBillableEntryId: string | null;
  createdAt: string;
  updatedAt: string;
  /** `AM-404` (D6): left unbilled by "No facturarlo" of its discarded charge, with the reason chosen. */
  notBillable?: boolean;
  nonBillableReason?: EntityReference | null;
  /** `AM-432` (D1): its column "Facturación", with the codes of a charge of a ticket. */
  billing: TicketChargeBilling;
}

/**
 * One period's bag of included hours (`FIN-14`, `DA-08`): what the opening allocated, what the
 * close consumed and let expire, and what a person, a reopening or a void moved. `remainingMinutes`
 * is the signed sum of the period's own lines - the balance of that bag alone.
 */
export interface ContractHourLedgerPeriod {
  billingPeriodId: string;
  name: string;
  /** `yyyy-MM-dd`. */
  startDate: string;
  endDate: string;
  status: BillingPeriodStatus;
  allocatedMinutes: number;
  consumedMinutes: number;
  expiredMinutes: number;
  adjustedMinutes: number;
  remainingMinutes: number;
  /**
   * What the period was given to spend: its allocation plus the adjustments a person made to it
   * (`AM-289`), without what a reopening or a void gave back.
   */
  includedMinutes: number;
  /**
   * The period holds a bag the contract no longer says: the allocation keeps what the contract said
   * the day the period opened, and editing the contract does not touch it (`AM-289`).
   */
  differsFromContract: boolean;
  /** `AM-459`: what a person moved with "Corregir el paquete", the "Correcciones" (signed). */
  correctedMinutes?: number;
  /** `AM-459`: what the system gave back (returning to open, a discarded charge), the "Devueltas". */
  returnedMinutes?: number;
  /**
   * `AM-636`: the "Excedente": what the close billed past the bag in a measured period, the estimate in the period open
   * today, none in another open one.
   */
  excessMinutes?: number | null;
}

/** What a correction of one open period's bag wrote, and the bag as it stands after it (`AM-289`). */
export interface ContractHourLedgerAdjustment {
  id: string;
  contractId: string;
  billingPeriodId: string;
  minutes: number;
  note: string;
  createdByUserId: string;
  createdAt: string;
  period: ContractHourLedgerPeriod;
}

/** The kinds of fee the opening of a period writes (`BillableSourceTypes.FromPeriodOpening`). */
export type PeriodFeeType = 'retainer_fee' | 'subscription' | 'fee_and_time_fee';

/**
 * The fee a period holds (`AM-296`): its entry, its kind, its rate as precise as the contract's monthly
 * amount, the amount rounded to the currency, and whether the close has yet to write it again - a fee a
 * reopening voided before `AM-288`.
 */
export interface ContractPeriodFeeEntry {
  entryId: string;
  unitRate: number;
  amount: number;
  currency: string;
  pendingRestore: boolean;
}

/** The fee a contract asks of each period today (`AM-296`): its rate, the monthly amount (`AM-454`: no kind). */
export interface ContractPeriodFeeExpected {
  unitRate: number;
}

/**
 * The fee of one open period beside the one the contract asks today (`AM-296`). The opening writes the
 * fee once, with the monthly amount of that day, and editing the contract does not touch it. The same
 * goes for the overage rate the period took when it opened (`AM-298`).
 */
export interface ContractPeriodFee {
  billingPeriodId: string;
  name: string;
  /** `yyyy-MM-dd`. */
  startDate: string;
  endDate: string;
  status: BillingPeriodStatus;
  /** What the close will charge; `null` when the period has no fee. */
  fee: ContractPeriodFeeEntry | null;
  /** The latest fee was voided by a person: it is said, never offered to be corrected. */
  voidedByHand: boolean;
  /** `null` when the contract's model charges no fee, or it has no monthly amount. */
  expected: ContractPeriodFeeExpected | null;
  differsFromContract: boolean;
  /**
   * The rate the close charges the excess of the period at, taken from the contract when it opened
   * (`AM-298`); `null` when the contract was not a retainer then.
   */
  overageRate: number | null;
  /** The rate the contract asks today; `null` unless it is a retainer. */
  expectedOverageRate: number | null;
  /** The contract asks a rate and the period holds another, or none. */
  overageRateDiffers: boolean;
}

/**
 * What the hours already recorded in the current period will take from its bag at the close
 * (pendiente 6): approved and pending alike, `unapprovedMinutes` being the pending part, and
 * `availableMinutes` the bag's balance less what is recorded (below zero, the excess). Raw minutes:
 * the close measures each hour with its own rounding.
 */
export interface ContractHourLedgerUsage {
  recordedMinutes: number;
  unapprovedMinutes: number;
  availableMinutes: number;
}

/**
 * The whole book of a contract's included hours (`FIN-14`, 07 §4.13). `balanceMinutes` is
 * `SUM(minutes)` of every line ever written to the book, never a running column; `periods` lists
 * only the ones that have a line in it, from the most recent, and `currentPeriod` is the `open`
 * one that covers today, if any, with `currentUsage` beside it.
 */
export interface ContractHourLedger {
  contractId: string;
  includedMinutesPerPeriod: number | null;
  balanceMinutes: number;
  currentPeriod: ContractHourLedgerPeriod | null;
  periods: ContractHourLedgerPeriod[];
  /** Absent on an answer that predates it, `null` when no period is current. */
  currentUsage?: ContractHourLedgerUsage | null;
}

/** How an hour is measured before it is priced (`FIN-34`). */
export interface BillingRule extends CatalogRow {
  name: string;
  contractId: string | null;
  workTypeId: string | null;
  serviceId: string | null;
  appliesToBillingModel: BillingModel | null;
  minBillableMinutes: number | null;
  minBillableScope: 'per_entry' | 'per_ticket_day';
  roundingMinutes: number | null;
  roundingMode: 'up' | 'nearest' | 'none';
  multiplierFactor: number;
  priorityOrder: number;
  /** Always present here: a rule is retired by deactivating it, never by deleting the row. */
  isActive: boolean;
  createdAt: string;
}

export const billingRulesApi = catalogApi<BillingRule>('/api/v1/billing-rules');

export const contractsApi = {
  ...catalogApi<Contract>('/api/v1/contracts'),
  get: (id: string, signal?: AbortSignal) =>
    apiFetch<Contract>(`/api/v1/contracts/${id}`, { signal }),
  /** The state machine of 05f §3 moves by command, never by a field of the body (`AM-116`). */
  activate: (id: string) =>
    apiFetch<Contract>(`/api/v1/contracts/${id}/activate`, { method: 'POST' }),
  expire: (id: string) => apiFetch<Contract>(`/api/v1/contracts/${id}/expire`, { method: 'POST' }),
  terminate: (id: string) =>
    apiFetch<Contract>(`/api/v1/contracts/${id}/terminate`, { method: 'POST' }),
  services: (id: string, params: ListParams, signal?: AbortSignal) =>
    fetchPage<ContractService>(`/api/v1/contracts/${id}/services`, params, signal),
  hourLedger: (id: string, signal?: AbortSignal) =>
    apiFetch<ContractHourLedger>(`/api/v1/contracts/${id}/hour-ledger`, { signal }),
  /**
   * Moves the bag of one open period by hand, with a reason (`AM-289`); `minutes` is signed.
   * `expectedIncludedMinutes` is the bag the difference was computed from: the API refuses it with
   * `period_included_minutes_changed` when the period no longer holds it, so the same difference is
   * never added twice. `idempotencyKey` (`AM-18`), one per opening of the modal or the notice, makes
   * the click that follows a lost answer get the first answer back.
   */
  adjustHours: (
    id: string,
    body: {
      billingPeriodId: string;
      minutes: number;
      note: string;
      expectedIncludedMinutes: number;
    },
    idempotencyKey: string,
  ) =>
    apiFetch<ContractHourLedgerAdjustment>(`/api/v1/contracts/${id}/hour-ledger/adjustments`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  /** The fee of each open period of the contract beside the one the contract asks (`AM-296`). */
  periodFees: (id: string, signal?: AbortSignal) =>
    apiFetch<ContractPeriodFee[]>(`/api/v1/contracts/${id}/period-fees`, { signal }),
  /**
   * Brings the fee of one open period to what the contract asks (`AM-296`): the API voids the fee the
   * period holds and writes the contract's. `expectedMonthlyAmount` and `expectedFeeEntryId` are
   * what the console showed (`expected.unitRate` and `fee.entryId`, or `null`): the API refuses with `period_fee_changed` when any of them is no longer
   * so. `idempotencyKey` (`AM-18`) makes the click that follows a lost answer get the first answer back.
   */
  correctPeriodFee: (
    id: string,
    body: {
      billingPeriodId: string;
      expectedMonthlyAmount: number | null;
      expectedFeeEntryId: string | null;
    },
    idempotencyKey: string,
  ) =>
    apiFetch<ContractPeriodFee>(`/api/v1/contracts/${id}/period-fees/corrections`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  /**
   * Brings the overage rate of one open period to what the contract asks (`AM-298`).
   * `expectedOverageRate` and `expectedPeriodOverageRate` are what the console showed
   * (`expectedOverageRate` and `overageRate` of the period): the API refuses with
   * `period_overage_rate_changed` when either is no longer so. `idempotencyKey` (`AM-18`) makes the
   * click that follows a lost answer get the first answer back.
   */
  correctPeriodOverageRate: (
    id: string,
    body: {
      billingPeriodId: string;
      expectedOverageRate: number | null;
      expectedPeriodOverageRate: number | null;
    },
    idempotencyKey: string,
  ) =>
    apiFetch<ContractPeriodFee>(`/api/v1/contracts/${id}/period-overage-rate/corrections`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  fixedPriceItems: (id: string, params: ListParams, signal?: AbortSignal) =>
    fetchPage<ContractFixedPriceItem>(`/api/v1/contracts/${id}/fixed-price-items`, params, signal),
  addFixedPriceItem: (id: string, body: Record<string, unknown>) =>
    apiFetch<ContractFixedPriceItem>(`/api/v1/contracts/${id}/fixed-price-items`, {
      method: 'POST',
      body,
    }),
  /** A physical delete, refused once the item became money (`FIN-13d`). */
  removeFixedPriceItem: (id: string, itemId: string) =>
    deleteResource(`/api/v1/contracts/${id}/fixed-price-items/${itemId}`),
};

/**
 * A rate card with the seven dimensions of `FIN-06`. A dimension left null means "any", and
 * `specificityScore` - which the database computes and nothing else may - is what decides which of
 * the matching cards wins.
 */
export interface RateCard extends CatalogRow {
  name: string;
  organizationId: string | null;
  /** The name of the customer the card names, as {@link Contract.organizationName} (`AM-371`); `null` when it names none. */
  organizationName: string | null;
  contractId: string | null;
  serviceId: string | null;
  serviceOfferingId: string | null;
  technicianRoleId: string | null;
  agentUserId: string | null;
  workTypeId: string | null;
  specificityScore: number;
  isActive: boolean;
  createdAt: string;
}

/** The seven dimensions in the order of their weights, for a UI that has to show them all. */
export const RATE_DIMENSIONS = [
  'agentUserId',
  'technicianRoleId',
  'serviceOfferingId',
  'serviceId',
  'contractId',
  'organizationId',
  'workTypeId',
] as const;

export type RateDimension = (typeof RATE_DIMENSIONS)[number];

/**
 * One stretch of a card's life. `lastDayCovered` is the last day the version governs, as a person
 * reads it: the database stores an exclusive bound and the API converts, so the console never sees
 * the bound and cannot misread it (`FIN-10b`).
 */
export interface RateCardVersion {
  id: string;
  rateCardId: string;
  billingRate: number | null;
  costRate: number | null;
  currency: string;
  /** `yyyy-MM-dd`. */
  effectiveFrom: string;
  lastDayCovered: string | null;
  note: string | null;
  eTag: string;
  createdAt: string;
  updatedAt: string;
}

export const rateCardsApi = {
  ...catalogApi<RateCard>('/api/v1/rate-cards'),
  versions: (id: string, params: ListParams, signal?: AbortSignal) =>
    fetchPage<RateCardVersion>(`/api/v1/rate-cards/${id}/versions`, params, signal),
  createVersion: (id: string, body: Record<string, unknown>) =>
    apiFetch<RateCardVersion>(`/api/v1/rate-cards/${id}/versions`, { method: 'POST', body }),
  closeVersion: (id: string, versionId: string, body: Record<string, unknown>, etag: string) =>
    patchResource<RateCardVersion>(`/api/v1/rate-cards/${id}/versions/${versionId}`, body, etag),
  removeCard: (id: string) => deleteResource(`/api/v1/rate-cards/${id}`),
};

/** The six states of a period (`00` §5.10). Only `open` can be closed, and only `under_review` approved. */
export const BILLING_PERIOD_STATUSES = [
  'open',
  'processing',
  'under_review',
  'approved',
  'invoiced',
  'closed',
] as const;

export type BillingPeriodStatus = (typeof BILLING_PERIOD_STATUSES)[number];

/** What the close produced, in the currency of the contract. Absent while the period is still open. */
export interface BillingPeriodSummary {
  billableEntriesCount: number;
  totalAmount: number;
  currency: string;
  /** `AM-451`: null when the contract's model has no bag; net of what was given back. */
  includedMinutesConsumed: number | null;
  overageMinutes: number | null;
}

/**
 * The work of the window the close could not turn into money (11 §7.6). The time pending its approval,
 * the rejected time (`AM-392`), the unresolved rates and the work without cover block the approval -
 * each means the total is known to be incomplete - and the rest only warn.
 */
export interface BillingPeriodExceptions {
  unapprovedTimeEntries: number;
  /** `AM-392`: the billable hours of the window waiting for the close that were rejected; blocking. */
  rejectedTimeEntries: number;
  unresolvedRates: number;
  unresolvedCosts: number;
  workWithoutContract: number;
  pendingFixedPriceItems: number;
  /** `AM-331`: the charge lines of the contract's tickets the next close will take; informative. */
  pendingTicketCharges: number;
  /** `AM-690` and `AM-604` (C31-54): the time each row of hours adds up, the "Duración" of its hours, in minutes. */
  unapprovedMinutes: number;
  rejectedMinutes: number;
  unresolvedRateMinutes: number;
  unresolvedCostMinutes: number;
  workWithoutContractMinutes: number;
  /** `AM-636`: the rows that block the approval, by their field names: the one definition the server keeps. */
  blocking?: string[];
}

/**
 * `AM-552` (decision 123 B): the rows of hours of the exceptions of a period, as the filter `exception` of
 * `GET /time-entries` names them; each row opens the hours it counts.
 */
export const PERIOD_EXCEPTIONS = [
  'unapproved',
  'rejected',
  'unresolved_rate',
  'without_contract',
  'unresolved_cost',
] as const;

export type PeriodException = (typeof PERIOD_EXCEPTIONS)[number];

/**
 * `AM-723` (decision 123 B): the rows of items and of parts and trips of the exceptions, which open the list of what
 * they count (`GET /billing-periods/pending-fixed-price-items` and `.../pending-ticket-charges`).
 */
export const PERIOD_EXCEPTION_LINES = [
  'pending_fixed_price_items',
  'pending_ticket_charges',
] as const;

export type PeriodExceptionLine = (typeof PERIOD_EXCEPTION_LINES)[number];

/** `PeriodPendingItem` (`AM-723`): a fixed price item the row "Ítems pactados por facturar" counts. */
export interface PeriodPendingItem {
  id: string;
  contractId: string;
  contractNumber: string;
  contractName: string;
  description: string;
  amount: number;
  currency: string;
  recognitionTrigger: string;
  ticketId: string | null;
  ticketNumber: string | null;
}

/** `PeriodPendingCharge` (`AM-723`): a part or trip the row "Repuestos y viáticos por facturar" counts. */
export interface PeriodPendingCharge {
  id: string;
  ticketId: string;
  ticketNumber: string;
  contractId: string;
  contractNumber: string;
  workDate: string;
  kind: string;
  description: string;
  quantity: number;
  unitRate: number;
  amount: number;
  currency: string;
}

/** One contract's window of work (`FIN-39`): a period is never shared by two contracts. */
export interface BillingPeriod extends CatalogRow {
  contractId: string;
  organizationId: string;
  /** The customer's name, on every answer about a period, as {@link Contract.organizationName} (`AM-371`). */
  organizationName: string | null;
  name: string;
  startDate: string;
  endDate: string;
  status: BillingPeriodStatus;
  openedAt: string;
  processedAt: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  invoicedAt: string | null;
  closedAt: string | null;
  /** The close the period is in, or went through, was queued by the automatic close of its contract; false while it is open. */
  automaticClose: boolean;
  summary: BillingPeriodSummary | null;
  exceptions: BillingPeriodExceptions | null;
  createdAt: string;
}

/** What the API answers to a close: the work is handed over, not done (07 §5.7). */
export interface BillingPeriodQueued {
  billingPeriodId: string;
  status: BillingPeriodStatus;
  jobExecutionId: string;
  idempotencyKey: string;
  statusUrl: string;
}

export const BILLABLE_SOURCE_TYPES = [
  'time_entry',
  'fixed_price',
  'subscription',
  'retainer_fee',
  'fee_and_time_fee',
  'included_hours_consumption',
  'overage',
  'reversal',
  'time_entry_adjustment',
  'ticket_charge',
] as const;

export type BillableSourceType = (typeof BILLABLE_SOURCE_TYPES)[number];

export const BILLABLE_ENTRY_STATUSES = [
  'draft',
  'grouped',
  'billed',
  'voided',
  'reversed',
] as const;

export type BillableEntryStatus = (typeof BILLABLE_ENTRY_STATUSES)[number];

/**
 * One line of money the close produced (05f §5.2), with its snapshot frozen whole (`FIN-27`). The
 * work of a ticket billed without a contract (`AM-329`) has neither a contract nor a period.
 */
export interface BillableEntry extends CatalogRow {
  billingPeriodId: string | null;
  contractId: string | null;
  organizationId: string;
  /** The customer's name, on every answer about an entry, as {@link Contract.organizationName} (`AM-371`). */
  organizationName: string | null;
  sourceType: BillableSourceType;
  timeEntryId: string | null;
  sourceSplitSequence: number | null;
  ticketId: string | null;
  agentUserId: string | null;
  workTypeId: string | null;
  serviceId: string | null;
  workDate: string | null;
  isRetroactive: boolean;
  description: string | null;
  quantity: number;
  unit: string;
  baseUnitRate: number | null;
  multiplierFactor: number;
  unitRate: number | null;
  currency: string;
  amount: number | null;
  costAmount: number | null;
  status: BillableEntryStatus;
  invoiceLineId: string | null;
  reversesBillableEntryId: string | null;
  voidReason: string | null;
  createdAt: string;
  /** `AM-454`: the document the charge is in, for the column "Comprobante"; null when it is in none. */
  document?: ChargeDocument | null;
}

/** `AM-454`: the document a charge (or a part or trip line, through its charge) is in. */
export interface ChargeDocument {
  id: string;
  /** `null` while the document is a draft. */
  documentNumber: string | null;
  documentType: DocumentType;
  status: InvoiceStatus;
}

export const billingPeriodsApi = {
  ...catalogApi<BillingPeriod>('/api/v1/billing-periods'),
  /** `AM-723`: the items the row counts, of one period or, without one, of every period not archived. */
  pendingItems: (periodId: string | undefined, signal?: AbortSignal) =>
    apiFetch<{ items: PeriodPendingItem[] }>(
      `/api/v1/billing-periods/pending-fixed-price-items${periodId === undefined ? '' : `?periodId=${encodeURIComponent(periodId)}`}`,
      { signal },
    ),
  /** `AM-723`: the parts and trips the row counts, of one period or of every period not archived. */
  pendingCharges: (periodId: string | undefined, signal?: AbortSignal) =>
    apiFetch<{ items: PeriodPendingCharge[] }>(
      `/api/v1/billing-periods/pending-ticket-charges${periodId === undefined ? '' : `?periodId=${encodeURIComponent(periodId)}`}`,
      { signal },
    ),
  get: (id: string, signal?: AbortSignal) =>
    apiFetch<BillingPeriod>(`/api/v1/billing-periods/${id}`, { signal }),
  open: (body: Record<string, unknown>) =>
    apiFetch<BillingPeriod>('/api/v1/billing-periods', { method: 'POST', body }),
  /**
   * Hands the close over: the answer is `202` and the period is left `processing` while the worker
   * does the work. The key is the client's, and sending it again returns the same job instead of
   * enqueueing a second one (07 §1.11).
   */
  close: (id: string, idempotencyKey: string) =>
    apiFetch<BillingPeriodQueued>(`/api/v1/billing-periods/${id}/close`, {
      method: 'POST',
      body: {},
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  approve: (id: string) =>
    apiFetch<BillingPeriod>(`/api/v1/billing-periods/${id}/approve`, { method: 'POST', body: {} }),
  reopen: (id: string) =>
    apiFetch<BillingPeriod>(`/api/v1/billing-periods/${id}/reopen`, { method: 'POST', body: {} }),
  /** `invoiced → closed` (11 §7.4): the definitive close, once the period's own documents are settled. */
  finalize: (id: string) =>
    apiFetch<BillingPeriod>(`/api/v1/billing-periods/${id}/finalize`, { method: 'POST', body: {} }),
};

export const billableEntriesApi = {
  ...catalogApi<BillableEntry>('/api/v1/billable-entries'),
  /**
   * "Descartar el cargo" (`AM-403`, D6): the reason, what to do with what produced it and, with
   * `not_billable`, the tenant's reason for not billing it, optional.
   */
  void: (
    id: string,
    reason: string,
    outcome: DiscardOutcome = 'recalculate',
    nonBillableReasonId: string | null = null,
  ) =>
    apiFetch<BillableEntry>(`/api/v1/billable-entries/${id}/void`, {
      method: 'POST',
      body: { reason, outcome, nonBillableReasonId },
    }),
};

/**
 * The six states of a document (`AM-410`, D8). Only `draft` can still be edited or discarded, and a
 * discarded draft is `discarded`; "Vencido" is a mark worked out with today's date, not a state.
 */
export const INVOICE_STATUSES = [
  'draft',
  'issued',
  'partially_paid',
  'paid',
  'cancelled',
  'discarded',
] as const;

export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const DOCUMENT_TYPES = ['invoice', 'credit_note', 'debit_note'] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const PAYMENT_METHODS = [
  'bank_transfer',
  'cash',
  'check',
  'card',
  'direct_debit',
  'offset',
  'other',
] as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** One line of a document (07 §4.13), the snapshot of the billable entry it was grouped from. */
export interface InvoiceLine {
  id: string;
  lineNumber: number;
  description: string;
  ticketId: string | null;
  taskId: string | null;
  agentUserId: string | null;
  workTypeId: string | null;
  serviceId: string | null;
  workDate: string | null;
  quantity: number;
  unit: 'hour' | 'unit' | 'month';
  unitRate: number;
  currency: string;
  amount: number;
  taxRateId: string | null;
  taxRateFraction: number;
  taxAmount: number;
  /** `AM-444`: what the line comes from ("Origen"), the source of its charges; null on a line typed by hand. */
  sourceType?: BillableSourceType | null;
  /** `AM-444`: how many hours the line adds up ("suma de N horas"). */
  hourCount?: number;
  /** `AM-444`: where its alícuota came from; "unresolved" on a draft says "IVA a confirmar al emitir". */
  taxRateOrigin?: TaxRateOrigin | null;
  /** `AM-444`: the work behind the line ("Ver el trabajo"). */
  work?: InvoiceLineWork[] | null;
}

/** `AM-444`: the level an alícuota came from (model of billing, "IVA, alícuota de IVA y su origen"). */
export type TaxRateOrigin = 'exempt' | 'service' | 'organization' | 'general' | 'unresolved';

/** `AM-515` (C27-23): what a credit note would take off, in positive amounts, against the balance. */
export interface CreditNotePreview {
  currency: string;
  subtotal: number;
  taxes: { taxRateFraction: number; taxAmount: number }[];
  total: number;
  balance: number;
  exceedsBalance: boolean;
}

/** `AM-444`: one piece of work behind a line, with its ticket, agent and day. */
export interface InvoiceLineWork {
  entryId: string;
  sourceType: BillableSourceType;
  ticketId: string | null;
  ticketNumber: string | null;
  agentName: string | null;
  workDate: string | null;
  description: string | null;
  quantity: number;
  unit: 'hour' | 'unit' | 'month';
  /** `AM-695` (C11-61): the unit price of the charge, its base price and the multiplier of its rule. */
  unitRate?: number | null;
  baseUnitRate?: number | null;
  multiplierFactor?: number;
}

/** `AM-444`: "Datos del cliente al emitir", the customer as the document copied it at issue. */
export interface InvoiceCustomerAtIssue {
  name: string | null;
  ivaCondition: string | null;
  taxIdType: string | null;
  taxId: string | null;
  address: string | null;
  billingEmails: string | null;
}

/** `AM-444`: why a document has no letter. */
export type NoLetterReason =
  'foreign_customer' | 'missing_customer_condition' | 'missing_issuer_condition';

/** One collection recorded against a document (`FIN-42`); a document's balance never goes below it. */
export interface InvoicePayment {
  id: string;
  amount: number;
  currency: string;
  paidOn: string;
  method: PaymentMethod;
  reference: string | null;
  note: string | null;
  recordedByUserId: string | null;
  /** `AM-442`: "Registrado por", the name of the person who recorded it; null for an integration. */
  recordedByName?: string | null;
  createdAt: string;
}

/**
 * A billing document - invoice, credit note or debit note (07 §4.13, 11 §9). `lines` and
 * `payments` travel only on the single-item read; the collection answers with them `null` so a
 * listing of documents never carries the weight of every one of their lines (07 §1.7).
 */
export interface Invoice extends CatalogRow {
  organizationId: string;
  /** The customer's name, on every answer about a document, as {@link Contract.organizationName} (`AM-367`, `AM-371`). */
  organizationName: string | null;
  /** `null` on the document of a ticket billed without a contract (`AM-329`), which has no period either. */
  contractId: string | null;
  billingPeriodId: string | null;
  ticketId: string | null;
  /** The ticket's own number, for the document's ficha to show and link instead of the internal id (P086). */
  ticketNumber: string | null;
  documentType: DocumentType;
  /** `null` while the document is still a draft (`CONV-50`): the number is assigned at emission. */
  documentNumber: string | null;
  status: InvoiceStatus;
  invoiceDate: string;
  /** Never set on a credit note. */
  dueDate: string | null;
  currency: string;
  subtotalAmount: number;
  taxAmount: number;
  /** Negative on a credit note (`FIN-35`). */
  totalAmount: number;
  paidAmount: number;
  creditedAmount: number;
  /**
   * Total minus collected minus credited (`FIN-42`); `AM-442`: null for a credit note, a cancelled
   * document, a draft and a discarded one (the console shows "-").
   */
  balance: number | null;
  paidAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  /** On a credit note, the document it corrects. */
  reversesInvoiceId: string | null;
  /** `AM-401` (D5): the document this draft replaces, cancelled to be issued again; only on a full detail. */
  replaces?: EntityReference | null;
  /** `AM-401`: the document that replaces this cancelled one; only on a full detail. */
  replacedBy?: EntityReference | null;
  /** `AM-417` (D8): the draft was discarded by the system ("Descartado por el sistema: (motivo)"). */
  discardedBySystem?: boolean;
  /** `AM-417`: the person who discarded the draft ("Descartado por (persona)"). */
  discardedByName?: string | null;
  notes: string | null;
  issuedByUserId: string | null;
  issuedAt: string | null;
  isLocked: boolean;
  lines: InvoiceLine[] | null;
  payments: InvoicePayment[] | null;
  createdAt: string;
  updatedAt: string;
  /** `AM-443`: the number and name of the contract, read with the document. */
  contractNumber?: string | null;
  contractName?: string | null;
  /** `AM-443`: the name of the period. */
  billingPeriodName?: string | null;
  /** `AM-443`: on a note, the number of the document it corrects. */
  reversesNumber?: string | null;
  /** `AM-443`: who issued it ("Emitido el"). */
  issuedByName?: string | null;
  /** `AM-443`: A, B or C; null without a letter or on a draft. */
  fiscalLetter?: string | null;
  /** `AM-443`: "unauthorized" until the electronic invoice; null on a draft or a discarded one. */
  fiscalValidity?: 'unauthorized' | 'authorized' | null;
  /** `AM-443`: how the last send by email ended ("Último envío"). */
  lastDelivery?: InvoiceLastDelivery | null;
  /** `AM-443`: on a corrected document, its notes ("Notas de crédito y de débito"); only on a full detail. */
  correctionNotes?: InvoiceNoteReference[] | null;
  /** `AM-444`: the grouping its lines were armed with; null on a document armed before migration 0108. */
  lineGrouping?: string | null;
  /** `AM-444`: where its due date came from, with the days of the terms. */
  dueDateOrigin?: 'contract' | 'organization' | 'general' | 'manual' | null;
  dueDateDays?: number | null;
  /** `AM-444`: the internal reason of a credit note, never printed. */
  reason?: string | null;
  /** `AM-444`: why an issued document has no letter. */
  noLetterReason?: NoLetterReason | null;
  /** `AM-444`: the customer as the document copied it at issue; null before migration 0108. */
  customerAtIssue?: InvoiceCustomerAtIssue | null;
}

export interface InvoiceLastDelivery {
  status: InvoiceDeliveryStatus;
  requestedAt: string;
  sentAt: string | null;
}

export interface InvoiceNoteReference {
  id: string;
  documentType: DocumentType;
  documentNumber: string | null;
  status: InvoiceStatus;
  invoiceDate: string;
  totalAmount: number;
  currency: string;
}

/** What `/cancel` answers: `replacement` is the new draft only when a `reissue` was requested. */
/**
 * `AM-402` (D5): what Emitir does to a draft - its date and due date (none on a credit note), its
 * letter (none while an IVA condition is unknown), the hours that become "Facturada", the period that
 * becomes "Facturado" and, when the tenant sends on issue, the addresses it goes to.
 */
/** `AM-403` (D6): "Volver a calcularlo en el próximo cierre" or "No facturarlo". */
export type DiscardOutcome = 'recalculate' | 'not_billable';

export interface InvoiceIssuePreview {
  invoiceDate: string;
  dueDate?: string | null;
  fiscalLetter?: string | null;
  hourCount: number;
  closesPeriod?: EntityReference | null;
  sendsOnIssue: boolean;
  recipients: string[];
  /** `AM-444`: why it is going to go without a letter, when it is. */
  noLetterReason?: NoLetterReason | null;
  /**
   * `AM-555` (item 78): the due date set by hand the issue discards, because it falls before the date of the
   * document, and the terms the document falls due by instead.
   */
  discardedDueDate?: string | null;
  dueDateOrigin?: string | null;
  dueDateDays?: number | null;
}

export interface InvoiceCancelled {
  invoice: Invoice;
  replacement: Invoice | null;
}

/** Who asked for a send of a document by email (migration 0058): a person, or issuing it. */
export type InvoiceDeliverySource = 'manual' | 'on_issue';

/**
 * `pending` is queued or between two attempts of the worker; `skipped` was never attempted (the
 * send on issue found no billing address, `lastError` says so).
 */
export type InvoiceDeliveryStatus = 'pending' | 'sent' | 'failed' | 'skipped';

/** One send of a document by email, to the addresses fixed when it was asked for. */
export interface InvoiceDelivery {
  id: string;
  invoiceId: string;
  source: InvoiceDeliverySource;
  recipients: string[];
  status: InvoiceDeliveryStatus;
  attempts: number;
  lastError: string | null;
  requestedByUserId: string | null;
  requestedAt: string;
  sentAt: string | null;
  /** `AM-445`: "Pedido por", who asked for it or, on issue, who issued the document. */
  requestedByName?: string | null;
}

/** `GET /invoices/{id}/deliveries`: where a send goes unless told otherwise, and every send, the most recent first. */
export interface InvoiceDeliveries {
  defaultRecipients: string[];
  items: InvoiceDelivery[];
}

export const invoicesApi = {
  ...catalogApi<Invoice>('/api/v1/invoices'),
  get: (id: string, signal?: AbortSignal) =>
    apiFetch<Invoice>(`/api/v1/invoices/${id}`, { signal }),
  /** Emission is a command guarded by the ETag, never a field flipped through the merge-patch. */
  /** `AM-402`: what Emitir would do to the draft, for its confirmation to say. */
  issuePreview: (id: string) =>
    apiFetch<InvoiceIssuePreview>(`/api/v1/invoices/${id}/issue-preview`),
  issue: (id: string, etag: string, body: Record<string, unknown>) =>
    apiFetch<Invoice>(`/api/v1/invoices/${id}/issue`, {
      method: 'POST',
      body,
      headers: { 'If-Match': etag },
    }),
  /** Also how a draft is discarded (`09` §13.1): it stays `cancelled`, never issued. */
  cancel: (id: string, body: { cancelReason: string; reissue?: boolean }) =>
    apiFetch<InvoiceCancelled>(`/api/v1/invoices/${id}/cancel`, { method: 'POST', body }),
  /**
   * The key is the client's, and travels with the request so a double click or a retry after a
   * dropped response collects once, never twice (07 §1.11).
   */
  recordPayment: (id: string, body: Record<string, unknown>, idempotencyKey: string) =>
    apiFetch<Invoice>(`/api/v1/invoices/${id}/payments`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  createCreditNote: (id: string, body: Record<string, unknown>) =>
    apiFetch<Invoice>(`/api/v1/invoices/${id}/credit-notes`, { method: 'POST', body }),
  /** `AM-515`: what the note of the same request would take off, worked out by the server, writing nothing. */
  previewCreditNote: (id: string, body: Record<string, unknown>) =>
    apiFetch<CreditNotePreview>(`/api/v1/invoices/${id}/credit-notes/preview`, {
      method: 'POST',
      body,
    }),
  /**
   * The stored PDF of an issued document (`ISO-16`, `AM-175`): rendered once, by
   * `GenerateInvoiceDocumentJob`, and served here exactly as it was saved - never regenerated on
   * the client's behalf.
   */
  downloadDocument: (id: string): Promise<void> => downloadFile(`/api/v1/invoices/${id}/document`),
  /**
   * The PDF of an issued document by email (migration 0058): to the organization's billing
   * addresses, or to `to` instead. Answers `202` with the send `pending`: the worker mails it.
   */
  send: (id: string, body: { to?: string[] }) =>
    apiFetch<InvoiceDelivery>(`/api/v1/invoices/${id}/send`, { method: 'POST', body }),
  deliveries: (id: string, signal?: AbortSignal) =>
    apiFetch<InvoiceDeliveries>(`/api/v1/invoices/${id}/deliveries`, { signal }),
};

/**
 * What the "Facturar" of a ticket does with it (`AM-329`, `TicketInvoiceModes`): its hours go on a
 * document of their own without a contract, or through the open period of a contract that charges by
 * the hour; a contract of another model covers them on the document of the month; a contract by the
 * hour without an open period, or nothing approved and priced, leaves nothing to invoice. `AM-337`:
 * `no_contract` is also what a ticket whose contract by the hour waits for a period can bill now,
 * and `no_open_period` is only said when nothing else of the ticket can be billed. `AM-358`: a covered
 * ticket with work without a contract is `no_contract` too, and `covered_by_contract` is only said when
 * it has none.
 */
export const TICKET_INVOICE_MODES = [
  'no_contract',
  'contract_hourly',
  'covered_by_contract',
  'no_open_period',
  'nothing',
] as const;

export type TicketInvoiceMode = (typeof TICKET_INVOICE_MODES)[number];

/** The contract that decides the mode, when there is one. */
export interface TicketInvoiceContract {
  id: string;
  contractNumber: string;
  name: string;
  billingModel: BillingModel;
  billingTrigger: Contract['billingTrigger'];
}

/** One hour the pass would take, measured and valued as the close values it. */
export interface TicketInvoiceHour {
  timeEntryId: string;
  /** `yyyy-MM-dd`. */
  workDate: string;
  agentName: string;
  workTypeName: string;
  durationMinutes: number;
  billableMinutes: number;
  /** The final price of one hour, the multiplier applied: the "Precio unitario" of the line. */
  unitRate: number;
  currency: string;
  amount: number;
  /** `AM-456`: the base price ("Precio por hora") and the multiplier of the rule ("Multiplicador del precio"). */
  baseUnitRate?: number | null;
  multiplierFactor?: number;
  /** `AM-456`: the rule that measured the hour, said on the pointer over its time to bill. */
  billingRuleName?: string | null;
  /** `AM-695` (C21-32): the origin of the price, said on the pointer over the "Precio por hora". */
  priceOrigin?: string | null;
  rateCardName?: string | null;
  /** `yyyy-MM-dd`. */
  rateCardVersionFrom?: string | null;
  /** `AM-695` (C21-29): the IVA of the row at the alícuota of its line; none while it does not resolve. */
  estimatedTax?: number | null;
}

/** The work of one currency added up, before tax; `documentCount` says how many documents it leaves on. */
export interface TicketInvoiceTotal {
  currency: string;
  subtotal: number;
  /** `AM-406` (D7): the IVA and the total with it at the alícuota of the ticket today, estimated. */
  estimatedTax?: number | null;
  estimatedTotal?: number | null;
}

/**
 * `GET /tickets/{id}/invoice-preview` (`AM-329`): what the "Facturar" would write, without writing.
 * The three counts are the hours left out (waiting for an approval, without a price, already on a
 * draft); `canIssue` and `canRecordPayment` say whether this person may issue and collect in the
 * same step.
 */
export interface TicketInvoicePreview {
  ticketId: string;
  ticketNumber: string;
  organizationId: string;
  organizationName: string;
  mode: TicketInvoiceMode;
  contract: TicketInvoiceContract | null;
  hours: TicketInvoiceHour[];
  /** `AM-331`: the charge lines the pass would take, with the hours or on their own. */
  charges: TicketInvoiceCharge[];
  /** `AM-337`: minutes added to hours already billed without a contract, at the rate of the hour. */
  adjustments: TicketInvoiceAdjustment[];
  /** `AM-337`: the pending fixed price items of the ticket the pass of their contract charges. */
  fixedPriceItems: TicketInvoiceFixedPriceItem[];
  /**
   * `AM-346`: what of the ticket is already a draft entry in the open period of a contract by the hour
   * (an item its resolution recognised, or what a discarded document gave back), which the pass of
   * that contract gathers on the ticket's document with the rest.
   */
  recognisedEntries: TicketInvoiceRecognisedEntry[];
  /**
   * Per currency, hours, charge lines, adjustments, fixed price items and recognised entries together,
   * before tax.
   */
  totals: TicketInvoiceTotal[];
  /**
   * The documents the pass would write: one per contract that takes something of the ticket in its
   * open period, and one per currency of what leaves without a contract. Two can share a currency, so
   * the collection in the same step is offered only when this is one.
   */
  documentCount: number;
  pendingApprovalCount: number;
  unresolvedRateCount: number;
  alreadyInDraftCount: number;
  /** `AM-331`: pending lines in a currency the contract that governs does not charge in; they wait. */
  skippedChargeCount: number;
  /**
   * `AM-337`: the contracts by the hour with work of the ticket and no open period; that work waits.
   * `AM-358`: with `no_contract`, also the contracts that cover the ticket (`covered_by_contract`), whose
   * work waits for the document of their period while the work without a contract is billed now. The
   * answer of the pass names the same ones, with the same counts.
   */
  waitingContracts: TicketInvoiceWaitingContract[];
  canIssue: boolean;
  canRecordPayment: boolean;
  /**
   * `AM-358`: the contract with a monthly fee whose close takes the hours without a contract listed here
   * if they are not billed now, and how many of them (those in its currency, dated up to the end of a
   * period of it still to close); null when none does.
   */
  monthlyInvoice: TicketInvoiceMonthlyInvoice | null;
  /**
   * `AM-389`: the hours whose "Facturación" is "Espera aprobación" (pending or rejected, with a price or
   * covered by a contract): the dialog lists them under the ones it takes, with no amount and out of
   * the total.
   */
  awaitingApproval: TicketInvoiceWaitingHour[];
  /**
   * `AM-694` (C21-12, C21-14, C21-15, C21-18): the other hours of the ticket meant to be charged, each with the
   * path it takes instead of this document. Never valued nor added to the totals.
   */
  otherHours: TicketInvoiceOtherHour[];
  /** `AM-694` (C21-16): the pending parts and trips this document does not take, each with its path. */
  otherCharges: TicketInvoiceOtherCharge[];
  /** `AM-694`: the pending agreed items of the ticket this document does not take, each with its path. */
  otherItems: TicketInvoiceOtherItem[];
  /**
   * `AM-780` (C21-08): what Emitir would settle on the documents of the pass, said before issuing; none without the
   * permission to issue. `AM-843`: also with several documents.
   */
  issue?: TicketInvoiceIssueFacts | null;
}

/** `AM-780` (C21-08): the date, due date, letter and addresses of the documents "Facturar" would issue. */
export interface TicketInvoiceIssueFacts {
  /** `yyyy-MM-dd`. */
  invoiceDate: string;
  /** `yyyy-MM-dd`: the earliest due date of the documents (`AM-843`). */
  dueDate: string;
  fiscalLetter: string | null;
  noLetterReason: string | null;
  sendsOnIssue: boolean;
  recipients: string[];
  /** `AM-843`: how many documents the pass writes (two currencies or two contracts). */
  documents: number;
  /** `AM-843`: the due date of the documents of each contract; no number for the work without one. */
  dueDates: { contractNumber: string | null; dueDate: string }[];
}

/**
 * `AM-694`: the path of a row of "Facturar" that does not leave on its document: to the document of the period
 * of its contract, waiting for an open period of it, of a contract that is not Vigente, without a price, already
 * in a draft, in another currency than the contract, or of a contract that charges nothing.
 */
export type TicketInvoicePath =
  | 'period_invoice'
  | 'waiting_open_period'
  | 'contract_not_active'
  | 'no_price'
  | 'in_draft'
  | 'other_currency'
  | 'contract_non_billable';

/** `AM-694`: an hour the document does not take; the reason of a "Sin precio" and the document of an "En borrador". */
export interface TicketInvoiceOtherHour {
  timeEntryId: string;
  /** `yyyy-MM-dd`. */
  workDate: string;
  agentName: string;
  workTypeName: string;
  durationMinutes: number;
  path: TicketInvoicePath;
  contractNumber: string | null;
  noPriceReason: string | null;
  document: ChargeDocument | null;
}

/** `AM-694`: a pending part or trip the document does not take. */
export interface TicketInvoiceOtherCharge {
  ticketChargeId: string;
  /** `yyyy-MM-dd`. */
  workDate: string;
  kind: string;
  description: string;
  quantity: number;
  unitRate: number;
  currency: string;
  amount: number;
  path: TicketInvoicePath;
  contractNumber: string | null;
}

/** `AM-694`: a pending agreed item the document does not take. */
export interface TicketInvoiceOtherItem {
  contractFixedPriceItemId: string;
  description: string;
  contractNumber: string;
  path: TicketInvoicePath;
}

/** `AM-389`: an hour that waits for its approval; `unitRate` is the one it would take, when it has one. */
export interface TicketInvoiceWaitingHour {
  timeEntryId: string;
  /** `yyyy-MM-dd`. */
  workDate: string;
  agentName: string;
  workTypeName: string;
  durationMinutes: number;
  unitRate: number | null;
  currency: string | null;
  /** `AM-780` (C21-13): `pending` says "Espera aprobación" and `rejected` "Rechazada". */
  approvalStatus?: 'pending' | 'rejected';
}

/** `AM-358`: where the hours without a contract go if they are not billed now. */
export interface TicketInvoiceMonthlyInvoice {
  contractId: string;
  contractNumber: string;
  billingModel: BillingModel;
  hourCount: number;
}

/** One adjustment the pass would bill (`AM-337`): the hour it corrects, its minutes and its reason. */
export interface TicketInvoiceAdjustment {
  timeEntryAdjustmentId: string;
  timeEntryId: string;
  /** `yyyy-MM-dd`, the day of the hour it corrects. */
  workDate: string;
  adjustmentMinutes: number;
  reason: string;
  unitRate: number;
  currency: string;
  amount: number;
  /** `AM-695` (C21-29): the IVA of the row at the alícuota of its line; none while it does not resolve. */
  estimatedTax?: number | null;
}

/** One fixed price item of the ticket the pass of its contract would charge (`AM-337`). */
export interface TicketInvoiceFixedPriceItem {
  contractFixedPriceItemId: string;
  contractId: string;
  contractNumber: string;
  description: string;
  currency: string;
  amount: number;
  /** `AM-695` (C21-29): the IVA of the row at the alícuota of its line; none while it does not resolve. */
  estimatedTax?: number | null;
}

/**
 * One entry of the ticket already written in the open period of a contract by the hour (`AM-346`), with
 * the amount it was written with. `sourceType` names its kind for when `description` is empty;
 * `workDate` (`yyyy-MM-dd`) is the day of the work, when it has one.
 */
export interface TicketInvoiceRecognisedEntry {
  billableEntryId: string;
  contractId: string;
  contractNumber: string;
  sourceType: BillableSourceType;
  description: string;
  workDate: string | null;
  currency: string;
  amount: number;
  /** `AM-695` (C21-29): the IVA of the row at the alícuota of its line; none while it does not resolve. */
  estimatedTax?: number | null;
}

/**
 * Why a contract's work of the ticket waits: it charges by the hour and has no open period (`AM-337`),
 * or it does not charge by the hour and covers the ticket, whose work without a contract is billed now
 * (`AM-358`).
 */
export type TicketInvoiceWaitingReason =
  | 'no_open_period'
  | 'covered_by_contract'
  // `AM-780` (C21-15): only in the answer of the pass, a contract of the ticket's work that is not Vigente.
  | 'contract_not_active';

/**
 * A contract whose work of the ticket waits - for an open period, or for the document of its period -
 * and what of it: hours, charge lines in its currency and fixed price items.
 */
export interface TicketInvoiceWaitingContract {
  contractId: string;
  contractNumber: string;
  billingModel: BillingModel;
  reason: TicketInvoiceWaitingReason;
  hourCount: number;
  chargeCount: number;
  fixedPriceItemCount: number;
}

/** One charge line of the ticket the pass would take (`AM-331`). */
export interface TicketInvoiceCharge {
  ticketChargeId: string;
  /** `yyyy-MM-dd`. */
  workDate: string;
  kind: TicketChargeKind;
  description: string;
  quantity: number;
  unitRate: number;
  currency: string;
  amount: number;
  /** `AM-695` (C21-29): the IVA of the row at the alícuota of its line; none while it does not resolve. */
  estimatedTax?: number | null;
}

/**
 * The collection recorded on the only document the pass wrote. Without `amount` it is the
 * document's total, and without `paidOn` the tenant's today.
 */
export interface TicketInvoicePayment {
  amount?: number | null;
  paidOn?: string | null;
  method?: PaymentMethod | null;
  reference?: string | null;
  note?: string | null;
}

/** The body of `POST /tickets/{id}/invoice`: a payment is only recorded on what the same step issues. */
export interface TicketInvoiceRequest {
  issue: boolean;
  payment?: TicketInvoicePayment | null;
  /** `AM-407` (D7): the rows whose "Incluir" was unticked; they stay "Por facturar". */
  exclude?: TicketInvoiceExclusion | null;
}

export interface TicketInvoiceExclusion {
  timeEntryIds: string[];
  ticketChargeIds: string[];
  timeEntryAdjustmentIds: string[];
  contractFixedPriceItemIds: string[];
  billableEntryIds: string[];
}

/**
 * What the pass wrote: one document per contract and currency, and, as the preview names them, the
 * contracts by the hour without an open period with what of the ticket still waits on each (`AM-337`).
 */
export interface TicketInvoices {
  invoices: Invoice[];
  waitingContracts: TicketInvoiceWaitingContract[];
}

/** The "Facturar" of a ticket (`AM-329`, `FIN-44` point 4), under `billing.invoice_tickets`. */
export const ticketInvoicingApi = {
  preview: (ticketId: string, signal?: AbortSignal) =>
    apiFetch<TicketInvoicePreview>(`/api/v1/tickets/${ticketId}/invoice-preview`, { signal }),
  /**
   * Writes the documents in one transaction and, when the body asks for it, issues them and collects
   * the only one. `idempotencyKey` (`AM-18`) makes the click that follows a lost answer get the first
   * answer back instead of invoicing twice.
   */
  invoice: (ticketId: string, body: TicketInvoiceRequest, idempotencyKey: string) =>
    apiFetch<TicketInvoices>(`/api/v1/tickets/${ticketId}/invoice`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
};

/** An amount in one currency: two currencies are never added up (`FIN-31`). */
export interface BillingHomeTotal {
  currency: string;
  amount: number;
}

/**
 * A finished ticket with something to invoice (`AM-342`): what its "Facturar" would charge now per
 * currency, before tax, as its preview says it. `finishedOn` (`yyyy-MM-dd`) is the day it was
 * resolved, or closed when it never was, in the tenant's calendar.
 */
export interface BillingHomeTicket {
  ticketId: string;
  ticketNumber: string;
  title: string;
  organizationId: string;
  organizationName: string;
  finishedOn: string | null;
  totals: BillingHomeTotal[];
}

/**
 * "Pendiente de facturar": the finished tickets with something to invoice. `count` and `totals` take
 * all of them; `items` are the first twenty, the longest finished first.
 */
export interface BillingHomePendingToInvoice {
  count: number;
  totals: BillingHomeTotal[];
  items: BillingHomeTicket[];
}

/** One invoice or debit note issued and not collected, with its balance (`AM-342`). */
export interface BillingHomeInvoice {
  invoiceId: string;
  documentNumber: string | null;
  documentType: DocumentType;
  organizationId: string;
  organizationName: string;
  /** `yyyy-MM-dd`. */
  invoiceDate: string;
  /** `yyyy-MM-dd`. */
  dueDate: string | null;
  balance: number;
  currency: string;
  isOverdue: boolean;
  /** `AM-476`: how many days late, by the tenant's today; null when it is not overdue. */
  daysOverdue?: number | null;
}

/**
 * "Sin cobrar": the documents still owed. The counts and `totals` (the balance per currency) take all
 * of them; `items` are the first twenty, the overdue first and those without a due date last.
 */
export interface BillingHomeUnpaid {
  count: number;
  overdueCount: number;
  totals: BillingHomeTotal[];
  items: BillingHomeInvoice[];
}

/**
 * `GET /billing/home` (`AM-342`): the box of Inicio. Each part is `null` when the reader may not see
 * it: `pendingToInvoice` takes `billing.invoice_tickets`, `unpaid` takes `invoices.view`.
 */
export interface BillingHome {
  pendingToInvoice: BillingHomePendingToInvoice | null;
  unpaid: BillingHomeUnpaid | null;
}

export const billingHomeApi = {
  get: (signal?: AbortSignal) => apiFetch<BillingHome>('/api/v1/billing/home', { signal }),
  /**
   * `GET /billing/pending-to-invoice` (`AM-350`): every ticket of "Pendiente de facturar", a page at a
   * time, with the rows and amounts of the box and in its order. The server plans only the tickets of
   * the page; `countExact` would plan all of them, so the console does not ask for it.
   */
  pendingToInvoice: (params: ListParams, signal?: AbortSignal) =>
    fetchPage<BillingHomeTicket>('/api/v1/billing/pending-to-invoice', params, signal),
};

/**
 * The four kinds of line a ticket can charge besides its hours (`AM-331`): a part, a product, a
 * travel expense, anything else. The list is `TicketChargeKinds` on the server; `closedLists.test.ts`
 * compares both.
 */
export const TICKET_CHARGE_KINDS = ['part', 'product', 'travel', 'other'] as const;

export type TicketChargeKind = (typeof TICKET_CHARGE_KINDS)[number];

/**
 * A line a ticket charges that no hour produces (`AM-331`). The currency is the server's choice (the
 * contract's or the customer's), so the console never sends it. Not null `consumedBillableEntryId`
 * says it was charged: from then on the line is read only and is corrected from the invoice.
 */
export interface TicketCharge {
  id: string;
  ticketId: string;
  organizationId: string;
  kind: TicketChargeKind;
  description: string;
  quantity: number;
  unitRate: number;
  /** What the line cost, for the margin; it never reaches the invoice. */
  unitCost: number | null;
  currency: string;
  /** `quantity` x `unitRate` at the decimals of the currency. */
  amount: number;
  /** `yyyy-MM-dd`. */
  workDate: string;
  consumedBillableEntryId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  eTag: string;
  /**
   * `AM-395`: where its money stands, with the codes of the column "Facturación" of the hours: nobody
   * charged it, its entry waits for a document or is on a draft, it is on an issued document, or that
   * document was cancelled without billing the work again.
   */
  billing: TicketChargeBilling;
  /** `AM-404` (D6): the reason "No facturarlo" of a discarded charge left the line unbilled with. */
  nonBillableReason?: EntityReference | null;
  /** `AM-456`: the document the line is in, through its charge, for the column "Comprobante". */
  document?: ChargeDocument | null;
}

/**
 * `AM-456` (C3): a line nobody charged yet is read against the contract the ticket has now:
 * `contract_non_billable` ("Contrato sin cargo") and `other_currency` ("Otra moneda que el contrato")
 * are never taken by a close and are not "Por facturar".
 */
export type TicketChargeBilling =
  | 'pending_billing'
  | 'in_draft'
  | 'billed'
  | 'invoice_cancelled'
  | 'not_billable'
  | 'contract_non_billable'
  | 'other_currency';

/** `GET /tickets/{id}/charges`: the lines and (`AM-456`) the currency a new line would take. */
export interface TicketChargeList {
  items: TicketCharge[];
  currency: string | null;
}

/** The body of `POST /tickets/{id}/charges`; without `workDate` the server takes the ticket's day, else today. */
export interface TicketChargeInput {
  kind: TicketChargeKind;
  description: string;
  quantity: number;
  unitRate: number;
  unitCost?: number | null;
  /** `yyyy-MM-dd`. */
  workDate?: string | null;
}

/** The merge patch of a line: only what changed travels, and a null `unitCost` clears it. */
export type UpdateTicketChargeInput = Partial<TicketChargeInput>;

/** The "Repuestos y viáticos" of a ticket (`AM-331`), under `tickets.manage` or `billing.invoice_tickets`. */
export const ticketChargesApi = {
  list: (ticketId: string, signal?: AbortSignal) =>
    apiFetch<{ items: TicketCharge[]; currency?: string | null }>(
      `/api/v1/tickets/${ticketId}/charges`,
      signal === undefined ? {} : { signal },
    ).then((response): TicketChargeList => ({
      items: response.items,
      currency: response.currency ?? null,
    })),
  create: (ticketId: string, body: TicketChargeInput) =>
    apiFetch<TicketCharge>(`/api/v1/tickets/${ticketId}/charges`, { method: 'POST', body }),
  update: (ticketId: string, chargeId: string, body: UpdateTicketChargeInput, etag: string) =>
    patchResource<TicketCharge>(`/api/v1/tickets/${ticketId}/charges/${chargeId}`, body, etag),
  remove: (ticketId: string, chargeId: string) =>
    deleteResource(`/api/v1/tickets/${ticketId}/charges/${chargeId}`),
};

/**
 * `AM-784` (decisión 35 de las 52, C30-44): the five states of the billing of a ticket, in the order of the model:
 * "Sin trabajo para facturar", "Por facturar", "En borrador", "Facturado en parte" and "Facturado".
 */
export const TICKET_BILLING_STATES = [
  'none',
  'pending_billing',
  'in_draft',
  'partially_billed',
  'billed',
] as const;
export type TicketBillingState = (typeof TICKET_BILLING_STATES)[number];

/** `AM-784` (C16-47): one document of the ticket, with its balance (only an issued invoice or debit note has one). */
export interface TicketBillingDocument extends ChargeDocument {
  currency: string;
  totalAmount: number;
  balance: number | null;
}

/** `AM-784` (C30-43): the block "Facturación del ticket". */
export interface TicketBillingSummary {
  ticketId: string;
  state: TicketBillingState;
  /** What "Facturar" would take today, per currency, before tax. */
  wouldInvoice: TicketInvoiceTotal[];
  documents: TicketBillingDocument[];
}

export const ticketBillingApi = {
  summary: (ticketId: string, signal?: AbortSignal) =>
    apiFetch<TicketBillingSummary>(`/api/v1/tickets/${ticketId}/billing`, { signal }),
  /** `AM-784` (C30-45): the state of each ticket asked for, for the column "Facturación" of the list. */
  states: (ticketIds: readonly string[], signal?: AbortSignal) =>
    apiFetch<{ items: { ticketId: string; state: TicketBillingState }[] }>(
      `/api/v1/tickets/billing-states?${ticketIds.map((id) => `ticketId=${encodeURIComponent(id)}`).join('&')}`,
      { signal },
    ),
};
